/**
 * Le shift de la démo — la porte d'entrée du métier, et la première scène à
 * filmer : « je rejoins le vocal, je démarre mon shift, les conversations
 * s'ouvrent ».
 *
 * En vrai, cet état vit dans Convex (`shiftGate.getGateForCurrentUser` + les
 * mutations `startShift` / `startBreak` / `endShiftEarly`). Ici il tient dans
 * un atom : la démo ne doit RIEN écrire dans la base d'une vraie agence, et
 * surtout pas une session de shift qui compterait dans les statistiques.
 *
 * La fenêtre est fabriquée au chargement du module de façon à contenir
 * `now` — donc l'écran s'ouvre toujours sur « Ton shift est ouvert », le
 * bouton « Démarrer mon shift » sous la main. Elle démarre 6 minutes plus tôt
 * (moins que la grâce de 10 min du composant) pour qu'aucun « tu es en retard »
 * ne vienne parasiter l'image.
 *
 * ⛔ Rien ici n'est persisté : recharger la page rend un shift neuf, prêt pour
 * une nouvelle prise. C'est le bouton « recommencer » de la démo, et il ne
 * coûte pas une ligne de code.
 */
import { atom } from "jotai";

import type { GateState, ShiftBlock } from "@/panels/conversations/shiftGateMath";

/** Durée du créneau de démonstration. 4 h = un vrai shift. */
const DUREE_MS = 4 * 60 * 60 * 1000;
/** De combien la fenêtre a déjà commencé quand on arrive. < 10 min (la grâce
 *  de retard du composant), sinon l'overlay affiche « tu es en retard de… ». */
const DEJA_COMMENCE_MS = 6 * 60 * 1000;

const DEBUT_MS = Date.now() - DEJA_COMMENCE_MS;
const FIN_MS = DEBUT_MS + DUREE_MS;

function minuteDuJour(ms: number): number {
  const d = new Date(ms);
  return d.getHours() * 60 + d.getMinutes();
}

/** Le bloc d'horaire fictif. Son `_id` ne part JAMAIS vers Convex : les
 *  boutons du shift sont court-circuités en démo (cf `ShiftGateOverlay`).
 *
 *  🪶 `lienVocal` vient du VRAI serveur Discord (query Convex
 *  `shiftGate.lienVocalDeDemonstration`), il n'est jamais écrit ici : le
 *  `_lib/shiftVoiceRole.ts` du dépôt interdit une copie de plus de la grille
 *  des shifts, et une copie apprendrait un jour un lien mort à la vidéo.
 *  `null` = on ne sait pas, et l'écran annonce alors l'exigence du vocal sans
 *  pointer un salon au hasard — comme en prod. */
function bloc(lienVocal: string | null): ShiftBlock {
  return {
    _id: "demo-shift",
    weekdays: [1, 2, 3, 4, 5, 6, 7],
    startMinute: minuteDuJour(DEBUT_MS),
    endMinute: minuteDuJour(FIN_MS),
    timezone: null,
    note: "Démo",
    discordVoiceUrl: lienVocal,
  };
}

export interface ShiftDemo {
  /** null = pas encore cliqué « Démarrer mon shift ». */
  demarreLeMs: number | null;
  /** null = pas en pause. */
  pauseDepuisMs: number | null;
  pauseCumuleeMs: number;
  /** true après « Arrêter » — le panneau se referme, comme en vrai. */
  termine: boolean;
}

export const shiftDemoAtom = atom<ShiftDemo>({
  demarreLeMs: null,
  pauseDepuisMs: null,
  pauseCumuleeMs: 0,
  termine: false,
});

/** Synthétise l'état que `computeGateState` rendrait pour ce shift fictif. */
export function etatDeLaPorte(
  shift: ShiftDemo,
  now: number,
  lienVocal: string | null = null,
): GateState {
  const BLOC = bloc(lienVocal);
  const fenetre = { block: BLOC, startMs: DEBUT_MS, endMs: FIN_MS };

  if (shift.termine || now >= FIN_MS) {
    // Shift fini : la porte se referme et annonce le prochain (demain, même
    // heure) — exactement ce que voit une chatteuse en fin de créneau.
    return {
      kind: "outside",
      next: {
        block: BLOC,
        startMs: DEBUT_MS + 24 * 60 * 60 * 1000,
        endMs: FIN_MS + 24 * 60 * 60 * 1000,
      },
    };
  }

  if (shift.demarreLeMs === null) return { kind: "pending-start", window: fenetre };

  // Même arithmétique que `computeGateState` : le quota de pause vaut un
  // sixième du créneau, et une pause en cours se décompte en direct.
  const quotaMs = Math.max(0, Math.floor((FIN_MS - DEBUT_MS) / 6));
  const enCoursMs =
    shift.pauseDepuisMs === null ? 0 : Math.max(0, now - shift.pauseDepuisMs);
  const utiliseMs = shift.pauseCumuleeMs + enCoursMs;
  return {
    kind: "active",
    window: fenetre,
    sessionStartedAt: shift.demarreLeMs,
    breakAllowanceMs: quotaMs,
    breakUsedMs: utiliseMs,
    breakRemainingMs: Math.max(0, quotaMs - utiliseMs),
    breakStartedAt: shift.pauseDepuisMs,
    effectiveEndMs: FIN_MS,
  };
}

/* ── Les quatre gestes du bandeau, en pur état ─────────────────────────── */

export function demarrer(shift: ShiftDemo): ShiftDemo {
  return { ...shift, demarreLeMs: Date.now(), termine: false };
}

export function mettreEnPause(shift: ShiftDemo): ShiftDemo {
  if (shift.pauseDepuisMs !== null) return shift;
  return { ...shift, pauseDepuisMs: Date.now() };
}

export function reprendre(shift: ShiftDemo): ShiftDemo {
  if (shift.pauseDepuisMs === null) return shift;
  return {
    ...shift,
    pauseCumuleeMs: shift.pauseCumuleeMs + Math.max(0, Date.now() - shift.pauseDepuisMs),
    pauseDepuisMs: null,
  };
}

export function arreter(shift: ShiftDemo): ShiftDemo {
  return { ...shift, termine: true, pauseDepuisMs: null };
}
